import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { forkJoin, of, Subscription, switchMap, take } from 'rxjs';
import { KbDataExtractionRequestService } from 'src/app/services/feature/data-extraction-request/kb-data-extraction-request.service';
import { RegisterService } from 'src/app/services/feature/register.service';

@Component({
  selector: 'app-kb-report-ecu-recipient',
  templateUrl: './kb-report-ecu-recipient.component.html',
  styleUrl: './kb-report-ecu-recipient.component.scss',
  standalone: false
})
export class KbReportECURecipientComponent implements OnInit, OnDestroy {
  private dataExtractionService = inject(KbDataExtractionRequestService);
  private fb = inject(FormBuilder);
  private registerService = inject(RegisterService);
  private cdr = inject(ChangeDetectorRef);

  sameAsAbove = false;
  allowNext = false;
  isAutoPopulated = false;

  pageFieldData: any;
  pageFieldData$ = this.registerService.getRegisterPageFields();
  accessRequestsWSList: any;
  countriesWSList: any;
  stateList: any;

  subscription = new Subscription();

  popupOpenCompanyName: boolean = false;
  popupOpenContactName: boolean = false;
  private timeout?: ReturnType<typeof setTimeout> | null = null;// Store the timeout ID
  reportECURecipientForm = this.fb.group({
    companyName: ['', Validators.required],
    contactName: ['', Validators.required],
    contactEmail: ['', [Validators.required, Validators.email]],
    contactPhone: ['', Validators.required],
    address1: ['', Validators.required],
    address2: [''],
    city: ['', Validators.required],
    state: [null as string | null, Validators.required],
    country: [null as string | null, Validators.required],
    zip: ['', Validators.required],
    customerReferenceNumber: ['']
  });
  constructor(
  ) {

  }

  ngOnInit(): void {
    this.setupCountryAndState();

    this.subscription.add(
      this.dataExtractionService.reportEcuRecipientCheckeBox$.subscribe((dataChecbox) => {
        this.sameAsAbove = dataChecbox;
        if (!this.sameAsAbove) {
          this.isAutoPopulated = false;
          this.allowNext = false;
        }
        else {
          this.isAutoPopulated = true;
          this.allowNext = true;
        }
        this.cdr.detectChanges();
      })
    );
    this.subscription.add(this.dataExtractionService.reportEcuRecipient$
      .subscribe(reportEcuRecipientdata => {
        this.setupForm(reportEcuRecipientdata);
      }));

    this.subscription.add(
      this.reportECURecipientForm.statusChanges.subscribe(status => {
        // Allow Next only if form is valid OR auto-populated by checkbox
        this.allowNext = (status === 'VALID') || this.isAutoPopulated;
        console.log(this.allowNext);
      })
    );
  }
  setupCountryAndState() {
    forkJoin({
      countrydata: this.registerService.getRegisterPageFields().pipe(take(1)),
      stateData: this.registerService.getRegions('US').pipe(take(1))
    }).subscribe({
      next: ({ countrydata, stateData }) => {
        this.pageFieldData = countrydata;
        this.countriesWSList = this.pageFieldData.countriesWSList;
        this.stateList = stateData;
        this.timeout = setTimeout(() => {
          this.reportECURecipientForm.get('country')?.setValue('US', { emitEvent: false });
          this.cdr.detectChanges();
        });
      },
      error: (err) => {
        console.error('Failed to load register fields/regions:', err);
      }
    });
  }
  back() {
    this.dataExtractionService.setreportEcuRecipientCheckbox(this.sameAsAbove);
    const payload = this.reportECURecipientForm.getRawValue?.() || this.reportECURecipientForm.value;
    this.dataExtractionService.setReportEcuRecipientData(payload);
    this.dataExtractionService.currentComponent$.next('requestor');

  }
  next() {
    const payload = this.reportECURecipientForm.getRawValue?.() || this.reportECURecipientForm.value;
    this.dataExtractionService.setReportEcuRecipientData(payload);
    this.dataExtractionService.currentComponent$.next('vehicleInformation');
  }

  onCountryChange(event: any, preSelectedState: string | null = null) {
    const rawdatacountryIso = event?.target?.value || event;
    const countryIso = rawdatacountryIso.split(":")[1].trim();
    if (!countryIso) {
      this.stateList = null;
      return;
    }
    this.registerService.getRegions(countryIso).pipe(take(1)).subscribe((data) => {
      this.stateList = data;
      //  If a state was provided, set AFTER loading
      if (preSelectedState) {
        this.timeout = setTimeout(() => {
          this.reportECURecipientForm.get('state')?.setValue(preSelectedState, { emitEvent: false });
        });
      }
    });
  }

  // ------------------------------------------------------------
  //  FIXED — Checkbox auto populate
  // ------------------------------------------------------------
  setupFormValuesOnCheckboxClick(event: any) {
    const checked = !!event?.target?.checked;
    this.sameAsAbove = checked;
    this.dataExtractionService.setreportEcuRecipientCheckbox(checked);
    if (!checked) {
      this.isAutoPopulated = false;
      this.allowNext = false;
      this.reportECURecipientForm.reset();
      this.reportECURecipientForm.enable();
      this.setupCountryAndState()
      this.reportECURecipientForm.updateValueAndValidity();
      return;
    }

    this.subscription.add(this.dataExtractionService.requestorData$.pipe(take(1),
      switchMap((customerData) => {
        if (!customerData) {
          return of({ customerData: null, stateData: null });
        }
        const { state, ...rest } = customerData;
        this.reportECURecipientForm.patchValue(rest, { emitEvent: false });
        const countryIso = customerData.country;

        // load state regions
        return this.registerService.getRegions(countryIso).pipe(take(1), switchMap((stateData) => of({ customerData, stateData })));
      })).subscribe(({ customerData, stateData }: any) => {
        if (!customerData) {
          this.allowNext = false;
          return;
        }

        this.stateList = stateData;
        const stateIso = customerData.state;
        // ⭐ Set state AFTER regions load
        this.timeout = setTimeout(() => {
          const validState = this.stateList?.regionsWSList?.some(
            (x: any) => x.isocode === stateIso
          );

          this.reportECURecipientForm.get('state')?.setValue(validState ? stateIso : null, { emitEvent: false });

          // disable UI
          const fields = ['companyName', 'contactName', 'contactName', 'contactEmail', 'contactPhone', 'address1', 'address2', 'city', 'state', 'country', 'zip'];
          fields.forEach(f => this.reportECURecipientForm.get(f)?.disable());
          this.reportECURecipientForm.updateValueAndValidity({ emitEvent: false });

          // enable next button
          this.allowNext = true;
          this.isAutoPopulated = true;
          this.cdr.detectChanges();
        });
      })
    );
  }

  // ------------------------------------------------------------
  // ⭐ FIXED — Existing Form Setup (edit mode)
  // ------------------------------------------------------------
  setupForm(data: any) {
    const countryIso = data?.country;
    const stateIso = data?.state;

    const { state, ...rest } = data;
    this.reportECURecipientForm.patchValue(rest);

    this.registerService
      .getRegions(countryIso)
      .pipe(take(1))
      .subscribe((stateData: any) => {
        this.stateList = stateData;

        const isValid = this.stateList?.regionsWSList?.some(
          (x: any) => x.isocode === stateIso
        );

        this.timeout = setTimeout(() => {
          this.reportECURecipientForm
            .get('state')
            ?.setValue(isValid ? stateIso : null, { emitEvent: false });

          if (this.sameAsAbove) {
            this.reportECURecipientForm.disable();
          }
          this.reportECURecipientForm.updateValueAndValidity();
          this.isAutoPopulated = true;
          this.allowNext = true;
        });
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }
  openPopupCompanyName() {
    this.popupOpenCompanyName = true;
  }

  closePopupCompanyName() {
    this.popupOpenCompanyName = false;
  }

  openPopupContactName() {
    this.popupOpenContactName = true;
  }

  closePopupContactName() {
    this.popupOpenContactName = false;
  }

}
